import React from 'react';
import { User, Briefcase, GraduationCap, FileText } from 'lucide-react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfo,
  ResumeSection,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 19 - 깔끔한 테이블 스타일의 전문적인 디자인
const Template19 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`w-full max-w-4xl mx-auto bg-white ${language === 'en' ? 'resume-english' : ''}`} style={{ fontFamily: "'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', sans-serif" }}>
      <div className="p-8">
        {/* 헤더 섹션 */}
        <header className="mb-6">
          <h1 className={getPlaceholderClass(hasRealData.name, "text-4xl font-bold text-black mb-1 tracking-wider", "text-4xl font-bold text-gray-400 mb-1 tracking-wider italic")}>
            {currentData.name}
          </h1>
          <p className={getPlaceholderClass(hasRealData.title, "text-lg text-gray-600 tracking-wide", "text-lg text-gray-400 tracking-wide italic")}>
            {currentData.title}
          </p>
        </header>

        {/* 요약 섹션 */}
        {currentData.summary && (
          <section className="mb-6">
            <div className="border-b border-gray-300 pb-1 mb-3">
              <h2 className="text-base font-bold text-black tracking-wider">{titles.introduction}</h2>
            </div>
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm text-gray-700 leading-relaxed"
            />
          </section>
        )}

        {/* 2열 레이아웃 */}
        <div className="flex gap-8">
          {/* 왼쪽 컬럼 - 40% */}
          <aside className="w-2/5">
            {/* 연락처 섹션 */}
            <section className="mb-6">
              <div className="border-b border-gray-300 pb-1 mb-3">
                <h2 className="text-base font-bold text-black tracking-wider">{titles.contact}</h2>
              </div>
              <div className="space-y-1 text-sm">
                {currentData.phone && (
                  <div className={getPlaceholderClass(hasRealData.phone, "text-gray-700", "text-gray-400 italic")}>
                    <span className="font-semibold">Phone:</span> {currentData.phone}
                  </div>
                )}
                {currentData.email && (
                  <div className={getPlaceholderClass(hasRealData.email, "text-gray-700", "text-gray-400 italic")}>
                    <span className="font-semibold">Email:</span> {currentData.email}
                  </div>
                )}
                {currentData.address && (
                  <div className={getPlaceholderClass(hasRealData.address, "text-gray-700", "text-gray-400 italic")}>
                    <span className="font-semibold">Address:</span> {currentData.address}
                  </div>
                )}
                {currentData.socialLinks && currentData.socialLinks.map((social, index) => (
                  <div key={index} className="text-gray-700">
                    <span className="font-semibold">{social.type}:</span> {social.url.replace(/^https?:\/\/(www\.)?/, '')}
                  </div>
                ))}
              </div>
            </section>

            {/* 학력 사항 섹션 */}
            <section className="mb-6">
              <div className="border-b border-gray-300 pb-1 mb-3">
                <h2 className="text-base font-bold text-black tracking-wider">{titles.education}</h2>
              </div>
              <EducationSection
                education={currentData.education}
                hasRealData={hasRealData}
                datePosition="bottom"
                degreeClassName="text-base font-semibold text-black"
                schoolClassName="text-sm text-gray-700"
                majorClassName="text-sm text-gray-700"
                dateClassName="text-sm text-gray-600"
                showMajor={true}
                itemClassName="mb-4 last:mb-0"
              />
            </section>

            {/* 보유 기술 섹션 */}
            <section className="mb-6">
              <div className="border-b border-gray-300 pb-1 mb-3">
                <h2 className="text-base font-bold text-black tracking-wider">{titles.skills}</h2>
              </div>
              <SkillSection
                skills={currentData.skills}
                hasRealData={hasRealData}
                variant="inline"
                className="text-sm text-gray-700"
                separator=", "
              />
            </section>

            {/* 자격증 및 수상 경력 섹션 */}
            {currentData.certificates.length > 0 && (
              <section className="mb-6">
                <div className="border-b border-gray-300 pb-1 mb-3">
                  <h2 className="text-base font-bold text-black tracking-wider">{titles.certificates}</h2>
                </div>
                <CertificateSection
                  certificates={currentData.certificates}
                  hasRealData={hasRealData}
                  className="space-y-2"
                  nameClassName="text-sm font-medium text-gray-700"
                  issuerClassName="text-sm text-gray-600"
                  dateClassName="text-sm text-gray-600"
                  dateFormat="separate"
                />
              </section>
            )}

            {/* Languages Section (if language data exists) */}
            {currentData.languages && currentData.languages.length > 0 && (
              <section className="mb-6">
                <div className="border-b border-gray-300 pb-1 mb-3">
                  <h2 className="text-base font-bold text-black tracking-wider">{titles.languages ? titles.languages : 'LANGUAGES'}</h2>
                </div>
                <div className="space-y-1 text-sm text-gray-700">
                  {currentData.languages.map((lang, index) => (
                    <div key={index}>
                      <span className="font-medium">{lang.language}</span>
                      <span className="text-gray-600 ml-2">({lang.level})</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </aside>

          {/* 오른쪽 컬럼 - 60% */}
          <main className="flex-1">
            {/* 핵심 역량 섹션 */}
            {currentData.keyStrengths.length > 0 && (
              <section className="mb-6">
                <div className="border-b border-gray-300 pb-1 mb-3">
                  <h2 className="text-base font-bold text-black tracking-wider">{titles.coreCompetencies}</h2>
                </div>
                <CoreCompetenciesSection
                  keyStrengths={currentData.keyStrengths}
                  hasRealData={hasRealData}
                  className="space-y-2"
                  bulletStyle="•"
                  bulletClassName="text-gray-600"
                  textClassName="text-sm text-gray-700 leading-relaxed"
                />
              </section>
            )}

            {/* 경력 사항 섹션 */}
            <section className="mb-6">
              <div className="border-b border-gray-300 pb-1 mb-3">
                <h2 className="text-base font-bold text-black tracking-wider">{titles.experience}</h2>
              </div>
              <ExperienceSection
                experiences={currentData.experiences}
                hasRealData={hasRealData}
                datePosition="inline"
                companyClassName="text-base font-semibold text-black"
                positionClassName="text-sm font-semibold text-gray-600"
                dateClassName="text-sm font-semibold text-gray-600"
                descriptionClassName="text-sm text-gray-700 mt-2 mb-3 leading-relaxed whitespace-pre-line"
                projectTitleClassName="text-sm font-semibold text-black mb-2"
                projectDescriptionClassName="text-sm text-gray-700 ml-2.5"
                itemClassName="mb-6 last:mb-0"
                showBulletForProjects={true}
                projectBulletStyle="•"
                projectBulletClassName="text-gray-600"
              />
            </section>

            {/* Papers Section */}
            {hasRealData.papers && (
              <section className="mb-6">
                <div className="border-b border-gray-300 pb-1 mb-3">
                  <h2 className="text-base font-bold text-black tracking-wider">{titles.papers}</h2>
                </div>
                <PaperSection
                  papers={currentData.papers}
                  hasRealData={hasRealData}
                  getPaperTypeText={getPaperTypeText}
                  titleClassName="text-sm font-semibold text-black"
                  authorsClassName="text-sm text-gray-600"
                  journalClassName="text-sm text-gray-700"
                  typeClassName="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                />
              </section>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Template19;